import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="product-sizes"
export default class extends Controller {
  static targets = ["hiddenField", "content", "button", "icon"];
  static values = {
    collapsible: { default: false, type: Boolean },
    open: { default: true, type: Boolean },
    warnAboutSizes: { default: false, type: Boolean },
  };

  connect() {
    // Find the select element for this group
    this.selectElement = this.element.querySelector("select");
    if (!this.selectElement) return;

    // Track previously selected values
    this.previouslySelected = new Set();

    // Listen for changes to the select element
    this.selectElement.addEventListener("change", this.handleSelectChange.bind(this));

    // Initialize accordion state if collapsible
    if (this.collapsibleValue) {
      this.updateAccordionState();
    }
  }

  disconnect() {
    if (this.selectElement) {
      this.selectElement.removeEventListener("change", this.handleSelectChange.bind(this));
    }
  }

  handleSelectChange(event) {
    const selected = Array.from(event.target.selectedOptions).map((option) => ({ label: option.label, value: option.value }));

    if (this.warnAboutSizesValue) {
      const SIZES_TO_WARN = ["adult_XS", "adult_3XL", "adult_4XL", "adult_5XL"];

      // Get currently selected values
      const currentlySelected = new Set(selected.map((option) => option.value));

      // Check only newly selected options for sizes that need warnings
      const newlySelected = selected.find((option) => SIZES_TO_WARN.includes(option.value) && !this.previouslySelected.has(option.value));
      if (newlySelected) {
        alert(
          `If you haven't already, please confirm availability of Adult ${newlySelected.label} with your Fundraising Coordinator before proceeding.`,
        );
      }
      // Update the previously selected set for next time
      this.previouslySelected = currentlySelected;
    }

    // Set values for hidden fields
    const selectedValues = selected.map((option) => option.value);
    this.updateHiddenFields(selectedValues);
  }

  // Accordion functionality
  toggle() {
    if (!this.collapsibleValue) return;

    this.openValue = !this.openValue;
    this.updateAccordionState();
  }

  updateAccordionState() {
    if (!this.collapsibleValue) return;

    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", this.openValue);
    }

    if (this.hasIconTarget) {
      if (this.openValue) {
        this.iconTarget.classList.add("rotate-180");
      } else {
        this.iconTarget.classList.remove("rotate-180");
      }
    }

    if (this.hasContentTarget) {
      if (this.openValue) {
        this.contentTarget.classList.remove("hidden");
        this.contentTarget.setAttribute("aria-hidden", "false");
      } else {
        this.contentTarget.classList.add("hidden");
        this.contentTarget.setAttribute("aria-hidden", "true");
      }
    }
  }

  updateHiddenFields(selectedValues) {
    // Update the hidden fields to reflect the selected state
    this.hiddenFieldTargets.forEach((hiddenFieldDiv) => {
      const sizeLabel = hiddenFieldDiv.dataset.sizeLabel;
      const isSelected = selectedValues.includes(sizeLabel);

      // Find the corresponding selected hidden field within this div
      const selectedField = hiddenFieldDiv.querySelector('input[name*="[selected]"]');
      if (selectedField) {
        selectedField.value = isSelected ? "1" : "0";
        // Trigger change event so other controllers can react
        selectedField.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }
}
